import React, { useState } from 'react';

interface SegmentEffort {
  segment_id?: string;
  segment_name?: string;
  elapsed_time_seconds?: number;
  moving_time_seconds?: number;
  distance_meters?: number;
  average_speed_mps?: number;
  average_heart_rate?: number;
  max_heart_rate?: number;
  average_cadence?: number;
  average_power_watts?: number;
  pr_rank?: number; // Personal record rank
  kom_rank?: number; // King/Queen of Mountain rank
}

interface ExpandableSegmentsProps {
  segments: SegmentEffort[];
  stravaActivityId?: string;
}

export const ExpandableSegments: React.FC<ExpandableSegmentsProps> = ({ segments, stravaActivityId }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!segments || segments.length === 0) {
    return null;
  }

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatPace = (speedMps: number): string => {
    if (!speedMps || speedMps === 0) return '--:--';
    const paceSecsPerKm = 1000 / speedMps;
    const mins = Math.floor(paceSecsPerKm / 60);
    const secs = Math.floor(paceSecsPerKm % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getRankBadge = (prRank?: number, komRank?: number) => {
    if (prRank === 1) {
      return <span className="text-xs bg-yellow-500 text-black px-2 py-0.5 rounded font-semibold">🏆 PR</span>;
    }
    if (komRank === 1) {
      return <span className="text-xs bg-purple-500 text-white px-2 py-0.5 rounded font-semibold">👑 KOM</span>;
    }
    if (prRank && prRank <= 3) {
      return <span className="text-xs bg-orange-500 text-white px-2 py-0.5 rounded">Top 3 PR</span>;
    }
    if (komRank && komRank <= 10) {
      return <span className="text-xs bg-blue-500 text-white px-2 py-0.5 rounded">Top 10</span>;
    }
    return null;
  };

  return (
    <div className="border-t border-gray-700 pt-3 mt-3">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between w-full text-left"
      >
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-300">🏁 Strava Segments</span>
          <span className="text-xs text-gray-400">
            {segments.length} segment{segments.length !== 1 ? 's' : ''}
          </span>
        </div>
        <span className="text-gray-400 text-sm">{isExpanded ? '▲' : '▼'}</span>
      </button>

      {isExpanded && (
        <div className="mt-3 space-y-3">
          {segments.map((segment, index) => (
            <div key={segment.segment_id || index} className="bg-gray-800 rounded-lg p-3 space-y-2">
              {/* Segment Header */}
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="text-sm font-medium text-white mb-1">
                    {segment.segment_name || `Segment ${index + 1}`}
                  </div>
                  {segment.distance_meters && (
                    <div className="text-xs text-gray-400">
                      {(segment.distance_meters / 1000).toFixed(2)} km
                    </div>
                  )}
                </div>
                {getRankBadge(segment.pr_rank, segment.kom_rank)}
              </div>

              {/* Segment Metrics Grid */}
              <div className="grid grid-cols-2 gap-2 text-xs">
                {segment.elapsed_time_seconds && (
                  <div>
                    <span className="text-gray-400">Time: </span>
                    <span className="text-white font-medium">{formatTime(segment.elapsed_time_seconds)}</span>
                  </div>
                )}
                {segment.average_speed_mps && (
                  <div>
                    <span className="text-gray-400">Pace: </span>
                    <span className="text-white font-medium">{formatPace(segment.average_speed_mps)}/km</span>
                  </div>
                )}
                {segment.average_heart_rate && (
                  <div>
                    <span className="text-gray-400">Avg HR: </span>
                    <span className="text-white font-medium">{segment.average_heart_rate} bpm</span>
                  </div>
                )}
                {segment.average_power_watts && (
                  <div>
                    <span className="text-gray-400">Avg Power: </span>
                    <span className="text-white font-medium">{segment.average_power_watts}W</span>
                  </div>
                )}
                {segment.average_cadence && (
                  <div>
                    <span className="text-gray-400">Avg Cadence: </span>
                    <span className="text-white font-medium">{segment.average_cadence} rpm</span>
                  </div>
                )}
              </div>

              {/* Leaderboard Ranks */}
              {(segment.pr_rank || segment.kom_rank) && (
                <div className="flex gap-3 text-xs pt-2 border-t border-gray-700">
                  {segment.pr_rank && (
                    <div>
                      <span className="text-gray-400">Your PR Rank: </span>
                      <span className="text-orange-400 font-medium">#{segment.pr_rank}</span>
                    </div>
                  )}
                  {segment.kom_rank && (
                    <div>
                      <span className="text-gray-400">Leaderboard: </span>
                      <span className="text-purple-400 font-medium">#{segment.kom_rank}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}

          {/* View on Strava Link */}
          {stravaActivityId && (
            <a
              href={`https://www.strava.com/activities/${stravaActivityId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="block text-center text-xs text-orange-400 hover:text-orange-300 transition-colors"
            >
              View full segment leaderboards on Strava →
            </a>
          )}
        </div>
      )}
    </div>
  );
};

